<template>
  <div
    class="dn-tooltip"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
  >
    <div
      ref="reference"
      class="label-container"
    >
      <slot />
    </div>
    <div
      v-show="show"
      ref="popper"
      class="tooltip"
      @mouseenter="showTooltip"
      @mouseleave="hideTooltip"
    >
      <slot name="content">
        <!-- eslint-disable vue/no-v-html -->
        <span v-html="tooltipText" />
      </slot>
      <div
        id="arrow"
        class="arrow"
        data-popper-arrow
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { type Ref } from 'vue';
import { createPopper, type Instance } from '@popperjs/core';

defineProps({
  tooltipText: {
    type: String,
    default: ''
  }
});

const show = ref(false);
const reference: Ref<null | Element> = ref(null);
const popper: Ref<null | HTMLElement> = ref(null);
const popperInstance: Ref<null | Instance> = ref(null);

function showTooltip () {
  show.value = true;
}

function hideTooltip () {
  show.value = false;
}

defineExpose({
  showTooltip,
  hideTooltip
});

function createPopperInstance () {
  if (popperInstance.value) {
    popperInstance.value.destroy();
  }
  if (!reference.value || !popper.value) {
    return;
  }

  popperInstance.value = createPopper(reference.value, popper.value, {
    placement: 'top',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 3]
        }
      }
    ]
  });
}

onMounted(() => {
  createPopperInstance();
});

onUnmounted(() => {
  if (popperInstance.value) {
    popperInstance.value.destroy();
  }
});

onUpdated(() => {
  createPopperInstance();
});
</script>

<style lang="scss">
.dn-tooltip {
  inline-size: min(var(--content-width), 100vw - calc(var(--site-padding) * 2));
  margin: 0 auto;
  z-index: 9;

  a {
    display: inline-block;
    text-decoration: none;
    color: var(--color-link-default);
  }

  a:hover,
  a:visited {
    color: var(--color-link-hover);
  }

  .label-container {
    width: fit-content;
  }

  .arrow,
  .arrow::before {
    position: absolute;
    width: 8px;
    height: 8px;
  }

  .arrow::before {
    content: '';
    background: var(--color-neutral-50);
    transform: rotate(45deg);
  }

  [data-popper-placement^='bottom'] {
    .arrow {
      top: -4px;
    }
  }

  [data-popper-placement^='top'] {
    .arrow {
      bottom: -4px;
    }
  }

  [data-popper-placement^='left'] {
    .arrow {
      right: -4px;
    }
  }

  [data-popper-placement^='right'] {
    .arrow {
      left: -4px;
    }
  }

  .tooltip {
    position: absolute;
    z-index: 4999;
    background: var(--color-neutral-50);
    color: var(--color-neutral-700);
    line-height: 150%;
    border-radius: var(--box-radius);
    padding: var(--row-gap);
    font-size: var(--typography-utility-s-font-size);
    width: 250px;
  }
}
</style>
